import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authService, AuthResponse } from '../services/auth';

type ProfileData = AuthResponse['user'];

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    authService.getCurrentUserFromApi()
      .then(user => {
        if (user) setProfile(user);
        else setError(t('profile.loadError'));
      })
      .catch(() => setError(t('profile.loadError')))
      .finally(() => setLoading(false));
  }, [t]);

  const updateField = (field: 'firstName' | 'lastName' | 'phoneNumber' | 'wechatId', value: string) => {
    setProfile(current => current ? { ...current, [field]: value } : current);
    setMessage('');
    setError('');
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!profile) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await authService.updateProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phoneNumber || '',
        wechatId: profile.wechatId || '',
      });
      await refreshUser();
      setMessage(t('profile.saved'));
    } catch {
      setError(t('profile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 disabled:text-gray-600';

  if (loading) return <main className="mx-auto max-w-2xl px-4 py-10" aria-busy="true">{t('common.loading')}</main>;

  return (
    <main className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="mb-6 text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
      {error && <p role="alert" className="mb-4 text-red-700">{error}</p>}
      {message && <p role="status" className="mb-4 text-green-700">{message}</p>}
      {profile && (
        <form onSubmit={submit} className="space-y-5 rounded-lg bg-white p-6 shadow">
          <label className="block text-sm font-medium text-gray-700">
            {t('profile.firstName')}
            <input required value={profile.firstName} onChange={event => updateField('firstName', event.target.value)} className={inputClass} />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            {t('profile.lastName')}
            <input required value={profile.lastName} onChange={event => updateField('lastName', event.target.value)} className={inputClass} />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            {t('profile.email')}
            <input disabled value={profile.email} className={inputClass} />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            {t('profile.school')}
            <input disabled value={profile.school || ''} className={inputClass} />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            {t('profile.phoneNumber')}
            <input type="tel" autoComplete="tel" value={profile.phoneNumber || ''} onChange={event => updateField('phoneNumber', event.target.value)} className={inputClass} />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            {t('profile.wechatId')}
            <input value={profile.wechatId || ''} onChange={event => updateField('wechatId', event.target.value)} className={inputClass} />
          </label>
          <button type="submit" disabled={saving} className="rounded-md bg-indigo-600 px-5 py-2 font-medium text-white disabled:opacity-60">
            {saving ? t('common.loading') : t('profile.save')}
          </button>
        </form>
      )}
    </main>
  );
};

export default Profile;
